import { FC, useCallback, useContext, useEffect, useState } from "react";

import { Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Spacing, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";

import { STANDARD_RESPONSIVE_LAYOUT } from "@/constants";
import { formatDate } from "capitalrx-utilities";

import { getPACaseDangerousAPI } from "@/api";
import { AuthenticationContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service";
import { getThemeURL } from "@/utils";
import { useNavigate } from "react-router";
import { CaseSearchForm } from "../PASearchForm";
import styles from "./PAListForm.module.scss";

export interface PAResultPanelProps {
  data: {
    case: {
      member_first_name: string;
      member_last_name: string;
      case_id: number;
      date_of_service: string;
      status: string;
      queue: string;
      drug_name: string;
      is_complete: boolean;
      effective_date: string;
      expiration_date: string;
    };
    case_outcome: {
      outcome: string;
    } | null;
  };
  singleMember: boolean;
  patientDOB?: string;
}
type DeviceType = "desktop" | "mobile";

type ResponsiveSpacing = {
  LayoutSpacing: Record<DeviceType, Spacing>;
};

export const PAResultPanel: FC<PAResultPanelProps> = ({ data, singleMember, patientDOB }) => {
  const [tag, setTag] = useState<JSX.Element | null>(null);
  const { getAuthToken } = useContext(AuthenticationContext);
  const RESPONSIVESPACING: ResponsiveSpacing = {
    LayoutSpacing: {
      desktop: "medium",
      mobile: "mini",
    },
  };
  const { Layout } = useResponsive(STANDARD_RESPONSIVE_LAYOUT);
  const { LayoutSpacing } = useResponsive(RESPONSIVESPACING);
  const navigate = useNavigate();

  useEffect(() => {
    if (data?.case.is_complete === false) {
      setTag(<Tag type="system-alternative-high-contrast">In Progress</Tag>);
    } else if (data?.case_outcome?.outcome?.toLowerCase() === "approved") {
      setTag(<Tag type="success-high-contrast">Approved</Tag>);
    } else if (data?.case_outcome?.outcome?.toLowerCase() === "denied") {
      setTag(<Tag type="error-high-contrast">Denied</Tag>);
    } else {
      setTag(null);
    }
  }, [data]);


  const getPACase = useCallback(async (case_id) => {

    try {
      // Find PA Case and process response
      const caseResponse = await getPACaseDangerousAPI({ case_id }, getAuthToken());
      const case_data: CaseSearchForm = {
        case_id: case_id,
        prescriber_npi: caseResponse.prescriber_npi,
        external_member_id: caseResponse.external_member_id,
        member_date_of_birth: patientDOB ?? caseResponse.member_date_of_birth,
      };
      const encodedParams = new URLSearchParams();
      for (const key in case_data) {
        if (case_data[key]) {
          encodedParams.append(key, case_data[key]);
        }
      }
      navigate({
        pathname: getThemeURL("PA_CASE"),
        search: encodedParams.toString(),
      });


      // analytics log case found
      logEvent(ANALYTIC_EVENTS.PA_CASE_SEARCH.found);

    } catch (error) {
      console.error(error);
    }


  }, [data, getAuthToken, navigate, patientDOB]);


  return (
    <button className={styles.ResultPanel}
      onClick={() => {
        getPACase(data.case.case_id);
      }}
    >
      <Stack spacing="small">
        <Shelf alignment="top">
          <Layout spacing={LayoutSpacing}>
            {singleMember ?
              <Subheading>Case ID {data.case.case_id}</Subheading> : <Subheading>{data.case.member_last_name}, {data.case.member_first_name}</Subheading>}
            {data?.case?.queue?.toLowerCase() === "outreach" && (
              <Tag type="warning-high-contrast">Action Required</Tag>
            )}
          </Layout>
          <Shelf.Spacer />
          {tag}
        </Shelf>
        <Layout spacing={LayoutSpacing}>
          {!singleMember && <Body>CASE ID {data.case.case_id}</Body>}
          <Body>{data.case.drug_name}</Body>
          {data?.case_outcome?.outcome?.toLowerCase() === "approved" && (
            <Body>{formatDate(data?.case?.effective_date, "MM/DD/YYYY")}-{formatDate(data?.case?.expiration_date, "MM/DD/YYYY")}</Body>
          )}
          <Shelf.Spacer />
          {data?.case?.date_of_service && (
            <Body size="small">Submitted {formatDate(data.case.date_of_service, "MM/DD/YYYY")}</Body>
          )}
        </Layout>
      </Stack>
    </button >
  );
};

export default PAResultPanel;
